// src/pages/LearningLogPage.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { app } from '../firebase';
import {
  getFirestore, collection, addDoc, getDocs,
  query, orderBy, limit, serverTimestamp, deleteDoc, doc
} from 'firebase/firestore';
import {
  Mic, Square, Save, ArrowLeft, Clock,
  BookOpen, Star, Trash2, CheckCircle,
  Zap, Calendar, TrendingUp, AlertCircle
} from 'lucide-react';

const db = getFirestore(app);

// 突破プランのDay→推奨カテゴリマッピング
const BREAKTHROUGH_CATEGORY_MAP = {
  LOGICAL_ARCHITECT:    ['practice','review','meeting','practice','review','practice','reflect'],
  EMPATHETIC_CONNECTOR: ['meeting','practice','review','meeting','practice','review','reflect'],
  BOLD_PIONEER:         ['practice','meeting','review','practice','creation','review','reflect'],
  FLEXIBLE_ARTISAN:     ['review','practice','creation','meeting','practice','review','reflect'],
};

const CATEGORIES = [
  { id: 'reading',   label: '読書・インプット',  emoji: '📚', color: '#3b82f6' },
  { id: 'practice',  label: '実践・アウトプット', emoji: '⚡', color: '#f59e0b' },
  { id: 'research',  label: '調査・リサーチ',    emoji: '🔍', color: '#8b5cf6' },
  { id: 'meeting',   label: '議論・ミーティング', emoji: '💬', color: '#10b981' },
  { id: 'creation',  label: '制作・クリエイティブ', emoji: '🎨', color: '#ef4444' },
  { id: 'review',    label: '振り返り・復習',     emoji: '🔄', color: '#6366f1' },
  { id: 'other',     label: 'その他',             emoji: '📝', color: '#6b7280' },
];


const TEMPLATES = [
  { id: 'basic',   label: '基本',   template: '【学習内容】__を学んだ。\n【時間】__時間集中できた。\n【気づき】__ということがわかった。\n【次にやること】次は__をやる。' },
  { id: 'deep',    label: '深掘り', template: '【テーマ】今日は__について学んだ。\n【なぜ重要か】__だから重要だと思った。\n【実践したこと】実際に__を試した。\n【うまくいったこと】__がうまくいった。\n【改善点】次回は__を改善する。' },
  { id: 'reflect', label: '振り返り', template: '【今日の学習】__を__時間学習した。\n【最大の気づき】一番の発見は__だった。\n【感情】学習中は__という気持ちだった。\n【明日へ】明日は__にチャレンジする。' },
];

const HOUR_PRESETS = [0.25, 0.5, 1, 1.5, 2, 3, 4];
const QUALITY_LABELS = ['', '集中できなかった', 'まあまあ', '普通', '充実していた', '最高の学習だった'];

const useSpeechRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      setIsSupported(true);
      const r = new SR();
      r.lang = 'ja-JP';
      r.continuous = true;
      r.interimResults = true;
      r.onresult = (e) => {
        let final = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) final += e.results[i][0].transcript;
        }
        if (final) setTranscript(prev => prev + final);
      };
      r.onerror = (e) => {
        setError(e.error === 'not-allowed' ? 'マイクへのアクセスが拒否されました。ブラウザの設定で許可してください。' : `音声認識エラー: ${e.error}`);
        setIsListening(false);
      };
      r.onend = () => setIsListening(false);
      recognitionRef.current = r;
    }
  }, []);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) return;
    setError(null);
    setTranscript('');
    try { recognitionRef.current.start(); setIsListening(true); }
    catch (e) { setError('音声認識の開始に失敗しました'); }
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setIsListening(false);
  }, []);

  return { isListening, transcript, error, isSupported, startListening, stopListening, setTranscript };
};

const StarRating = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
    {[1,2,3,4,5].map(s => (
      <button key={s} onClick={() => onChange(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, transform: s <= value ? 'scale(1.2)' : 'scale(1)', transition: 'transform 0.15s' }}>
        <Star size={22} fill={s <= value ? '#f59e0b' : 'none'} color={s <= value ? '#f59e0b' : '#d1d5db'} strokeWidth={1.5} />
      </button>
    ))}
    {value > 0 && <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 4 }}>{QUALITY_LABELS[value]}</span>}
  </div>
);

const LogCard = ({ log, onDelete }) => {
  const d = log.date?.toDate ? log.date.toDate() : new Date(log.date?.seconds * 1000 || Date.now());
  const cat = CATEGORIES.find(c => c.id === log.category) || CATEGORIES[CATEGORIES.length - 1];
  const hours = Number(log.hours || 0);
  return (
    <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12, animation: 'slideIn 0.25s ease' }}>
      <div style={{ background: cat.color + '18', borderRadius: 10, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{cat.emoji}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: cat.color, fontWeight: 700 }}>{cat.label}</span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short' })}</span>
        </div>
        {log.notes && <p style={{ margin: '0 0 6px', fontSize: 13, color: '#374151', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{log.notes}</p>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Clock size={11} color="#6b7280" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{hours.toFixed(1)}h</span>
          </div>
          {log.quality_rating > 0 && (
            <div style={{ display: 'flex', gap: 1 }}>
              {[1,2,3,4,5].map(s => <Star key={s} size={10} fill={s <= log.quality_rating ? '#f59e0b' : 'none'} color={s <= log.quality_rating ? '#f59e0b' : '#d1d5db'} />)}
            </div>
          )}
        </div>
      </div>
      <button onClick={() => onDelete(log.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', padding: 4 }} onMouseEnter={e => e.currentTarget.style.color='#ef4444'} onMouseLeave={e => e.currentTarget.style.color='#d1d5db'}>
        <Trash2 size={14} />
      </button>
    </div>
  );
};

export default function LearningLogPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hours, setHours] = useState(1);
  const [customHours, setCustomHours] = useState('');
  const [category, setCategory] = useState('practice');
  const [notes, setNotes] = useState('');
  const [quality, setQuality] = useState(0);
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [templateMode, setTemplateMode] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState('basic');
  const [templateFills, setTemplateFills] = useState({});
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [breakthroughInfo, setBreakthroughInfo] = useState(null);
  const { isListening, transcript, error: speechError, isSupported, startListening, stopListening, setTranscript } = useSpeechRecognition();

  useEffect(() => {
    if (!isListening && transcript) {
      setNotes(prev => prev ? prev + ' ' + transcript : transcript);
      setTranscript('');
    }
  }, [isListening]);

  const fetchLogs = useCallback(async () => {
    if (!user?.uid) return;
    setLogsLoading(true);
    try {
      const ref = collection(db, 'users', user.uid, 'logs');
      const q = query(ref, orderBy('date', 'desc'), limit(30));
      const snap = await getDocs(q);
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { setLogs([]); }
    finally { setLogsLoading(false); }
  }, [user?.uid]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // 突破プランの状態を読み込み
  useEffect(() => {
    try {
      const days = JSON.parse(localStorage.getItem('nobishiro_breakthrough_days') || '[]');
      const pace = parseFloat(localStorage.getItem('nobishiro_breakthrough_pace') || '1');
      const archetypeKey = localStorage.getItem('nobishiro_breakthrough_archetype') || 'LOGICAL_ARCHITECT';
      const nextStep = [1,2,3,4,5,6,7].find(d => !days.includes(d));
      if (nextStep) {
        const catMap = BREAKTHROUGH_CATEGORY_MAP[archetypeKey] || BREAKTHROUGH_CATEGORY_MAP.LOGICAL_ARCHITECT;
        const suggestedCat = catMap[nextStep - 1] || 'practice';
        const totalDays = Math.round(7 / pace);
        setBreakthroughInfo({ nextStep, suggestedCat, completed: days.length, totalDays, pace });
        // カテゴリを自動提案
        setCategory(suggestedCat);
      } else if (days.length >= 7) {
        setBreakthroughInfo({ completed: 7, totalDays: Math.round(7 / pace), done: true });
      }
    } catch(e) {}
  }, []);

  const stats = useMemo(() => {
    const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (weekStart.getDay() === 0 ? -6 : 1)); weekStart.setHours(0,0,0,0);
    let total = 0, weekly = 0;
    logs.forEach(log => {
      const h = Number(log.hours || 0);
      const d = log.date?.toDate ? log.date.toDate() : new Date(log.date?.seconds * 1000 || 0);
      total += h; if (d >= weekStart) weekly += h;
    });
    return { total, weekly, count: logs.length };
  }, [logs]);

  const handleSave = async () => {
    if (!user?.uid) return;
    const finalHours = customHours ? Number(customHours) : hours;
    if (!finalHours || finalHours <= 0) return;
    setSaving(true);
    try {
      await addDoc(collection(db, 'users', user.uid, 'logs'), {
        hours: finalHours, category, notes: notes.trim(),
        quality_rating: quality, date: new Date(logDate), createdAt: serverTimestamp(),
        breakthroughStep: breakthroughInfo?.nextStep || null,
      });
      // 突破プランとの連携: quality >= 3 かつ突破プランのカテゴリと一致した場合に自動完了提案
      if (breakthroughInfo?.nextStep && quality >= 3 && category === breakthroughInfo.suggestedCat) {
        const days = JSON.parse(localStorage.getItem('nobishiro_breakthrough_days') || '[]');
        if (!days.includes(breakthroughInfo.nextStep)) {
          const confirmed = window.confirm(
            `🔥 Step ${breakthroughInfo.nextStep} のアクション（${CATEGORIES.find(c=>c.id===breakthroughInfo.suggestedCat)?.label}）を実践しましたね！

突破プランのStep ${breakthroughInfo.nextStep} を完了済みにしますか？`
          );
          if (confirmed) {
            const newDays = [...days, breakthroughInfo.nextStep];
            localStorage.setItem('nobishiro_breakthrough_days', JSON.stringify(newDays));
            setBreakthroughInfo(prev => ({ ...prev, completed: newDays.length, nextStep: [1,2,3,4,5,6,7].find(d => !newDays.includes(d)) || null }));
          }
        }
      }
      setNotes(''); setQuality(0); setHours(1); setCustomHours('');
      setSaved(true); setTimeout(() => setSaved(false), 2500);
      fetchLogs();
    } catch (e) { alert('保存に失敗しました: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (logId) => {
    if (!confirm('このログを削除しますか？')) return;
    try { await deleteDoc(doc(db, 'users', user.uid, 'logs', logId)); setLogs(prev => prev.filter(l => l.id !== logId)); }
    catch (e) { alert('削除に失敗しました'); }
  };

  const finalHours = customHours ? Number(customHours) : hours;
  const selectedCat = CATEGORIES.find(c => c.id === category);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Hiragino Sans','Yu Gothic','Noto Sans JP',sans-serif", WebkitFontSmoothing: 'antialiased' }}>
      <style>{`
        @media (max-width: 480px) {
          .log-form-card { padding: 16px 14px !important; }
          .log-preset-btn { padding: 6px 10px !important; font-size: 11px !important; }
          .log-category-grid { grid-template-columns: repeat(4, 1fr) !important; gap: 6px !important; }
          .log-submit-btn { padding: 14px !important; font-size: 15px !important; }
        }
      `}</style>
      <style>{`@keyframes slideIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}@keyframes savedAnim{0%{opacity:0;transform:scale(0.8)}50%{opacity:1;transform:scale(1.05)}100%{opacity:1;transform:scale(1)}}*{box-sizing:border-box}input[type=range]{accent-color:#3b6fd4}`}</style>
      <div style={{ background: 'linear-gradient(135deg,#1e3a5f 0%,#1e40af 50%,#312e81 100%)', color: '#fff', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <ArrowLeft size={15} /> 戻る
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: '#93c5fd', letterSpacing: '0.08em' }}>NOBISHIROコンピテンシー®</div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>学習ログ記録</h1>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 800 }}>{stats.weekly.toFixed(1)}<span style={{ fontSize: 10, fontWeight: 400 }}>h</span></div><div style={{ fontSize: 9, color: '#93c5fd' }}>今週</div></div>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 800 }}>{stats.total.toFixed(0)}<span style={{ fontSize: 10, fontWeight: 400 }}>h</span></div><div style={{ fontSize: 9, color: '#93c5fd' }}>累計</div></div>
        </div>
      </div>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '16px 12px 60px' }}>
        <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #e5e7eb', padding: '22px 20px', marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          
      {/* 突破プラン連携バナー */}
      {breakthroughInfo && !breakthroughInfo.done && (
        <div style={{ background: 'linear-gradient(135deg,#7c3aed,#6366f1)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 20 }}>🔥</span>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 700 }}>突破プラン連携</div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>Step {breakthroughInfo.nextStep} を今日実践しよう</div>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>進捗</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{breakthroughInfo.completed}/7</div>
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 99, height: 5, overflow: 'hidden', marginBottom: 10 }}>
            <div style={{ background: '#fff', height: '100%', width: `${(breakthroughInfo.completed/7)*100}%`, borderRadius: 99, transition: 'width 0.4s' }} />
          </div>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '10px 12px', fontSize: 12, lineHeight: 1.6 }}>
            <span style={{ fontWeight: 700 }}>💡 推奨カテゴリ：</span>
            {CATEGORIES.find(c => c.id === breakthroughInfo.suggestedCat)?.emoji} {CATEGORIES.find(c => c.id === breakthroughInfo.suggestedCat)?.label}
            <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.85 }}>を選んで学習記録するとStep完了の確認が出ます</span>
          </div>
          <a href="/breakthrough" style={{ display: 'block', marginTop: 8, textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.9)', textDecoration: 'underline' }}>
            突破プランの詳細を確認 →
          </a>
        </div>
      )}
      {breakthroughInfo?.done && (
        <div style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🏆</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800 }}>全7ステップ突破！おめでとうございます！</div>
            <a href="/breakthrough" style={{ fontSize: 11, color: 'rgba(255,255,255,0.9)', textDecoration: 'underline' }}>次のチャレンジへ →</a>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setTemplateMode(true)} style={{ flex: 1, background: templateMode ? '#1e40af' : '#f3f4f6', color: templateMode ? '#fff' : '#6b7280', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>📝 テンプレート穴埋め</button>
        <button onClick={() => setTemplateMode(false)} style={{ flex: 1, background: !templateMode ? '#1e40af' : '#f3f4f6', color: !templateMode ? '#fff' : '#6b7280', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>✏️ 通常モード</button>
      </div>
      {templateMode && (
        <div style={{ background: '#f0f9ff', borderRadius: 14, border: '1.5px solid #bae6fd', padding: '18px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 12 }}>テンプレートを選んで穴埋めしよう</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {TEMPLATES.map(t => (
              <button key={t.id} onClick={() => { setSelectedTemplate(t.id); setTemplateFills({}); }} style={{ flex: 1, background: selectedTemplate === t.id ? '#0369a1' : '#fff', color: selectedTemplate === t.id ? '#fff' : '#374151', border: `1.5px solid ${selectedTemplate === t.id ? '#0369a1' : '#e5e7eb'}`, borderRadius: 8, padding: '7px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t.label}</button>
            ))}
          </div>
          {TEMPLATES.filter(t => t.id === selectedTemplate).map(t => {
            const parts = t.template.split('__');
            return (
              <div key={t.id}>
                <div style={{ background: '#fff', borderRadius: 10, padding: '14px', border: '1px solid #e0f2fe', marginBottom: 12 }}>
                  {parts.map((part, i) => (
                    <React.Fragment key={i}>
                      <span style={{ fontSize: 13, color: '#374151', lineHeight: 2.2, whiteSpace: 'pre-wrap' }}>{part}</span>
                      {i < parts.length - 1 && (
                        <input value={templateFills[i] || ''} onChange={e => setTemplateFills(prev => ({ ...prev, [i]: e.target.value }))} placeholder="ここに入力" style={{ display: 'inline-block', border: 'none', borderBottom: '2px solid #3b82f6', background: '#eff6ff', borderRadius: '4px 4px 0 0', padding: '2px 8px', fontSize: 13, color: '#1e40af', fontWeight: 600, minWidth: 80, outline: 'none' }} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <button onClick={() => { let result = t.template; Object.entries(templateFills).forEach(([, val]) => { result = result.replace('__', val || '___'); }); setNotes(result); setTemplateMode(false); }} style={{ width: '100%', background: 'linear-gradient(135deg,#1e40af,#3b6fd4)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>この内容でメモに反映する</button>
              </div>
            );
          })}
        </div>
      )}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}><Calendar size={13} color="#3b6fd4" /> 日付</label>
            <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)} style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 14, width: '100%', outline: 'none', color: '#111827' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}><Clock size={13} color="#3b6fd4" /> 学習時間</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {HOUR_PRESETS.map(h => (
                <button key={h} onClick={() => { setHours(h); setCustomHours(''); }} style={{ background: hours === h && !customHours ? '#3b6fd4' : '#f3f4f6', color: hours === h && !customHours ? '#fff' : '#374151', border: `1.5px solid ${hours === h && !customHours ? '#3b6fd4' : '#e5e7eb'}`, borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  {h < 1 ? `${h * 60}分` : `${h}h`}
                </button>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" min="0.1" max="24" step="0.1" value={customHours} onChange={e => setCustomHours(e.target.value)} placeholder="その他" style={{ border: `1.5px solid ${customHours ? '#3b6fd4' : '#e5e7eb'}`, borderRadius: 8, padding: '7px 10px', fontSize: 13, width: 80, outline: 'none', textAlign: 'center' }} />
                <span style={{ fontSize: 12, color: '#6b7280' }}>時間</span>
              </div>
            </div>
            <div style={{ background: 'linear-gradient(135deg,#eff6ff,#f0f9ff)', borderRadius: 10, padding: '10px 16px', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={16} color="#3b6fd4" />
              <span style={{ fontSize: 15, fontWeight: 800, color: '#1e40af' }}>{finalHours || 0} 時間の学習</span>
              <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 'auto' }}>累計 → {(stats.total + (finalHours || 0)).toFixed(1)}h</span>
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}><BookOpen size={13} color="#3b6fd4" /> カテゴリ</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 8 }}>
              {CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => setCategory(cat.id)} style={{ background: category === cat.id ? cat.color + '18' : '#f9fafb', border: `1.5px solid ${category === cat.id ? cat.color : '#e5e7eb'}`, borderRadius: 10, padding: '9px 12px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 16 }}>{cat.emoji}</span>
                  <span style={{ fontSize: 11, fontWeight: category === cat.id ? 700 : 400, color: category === cat.id ? cat.color : '#374151' }}>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#374151' }}><Zap size={13} color="#3b6fd4" /> 学習メモ <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>（何を学んだか）</span></label>
              {isSupported && (
                <button onClick={isListening ? stopListening : startListening} style={{ background: isListening ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#3b6fd4,#1e40af)', border: 'none', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, boxShadow: isListening ? '0 0 12px rgba(239,68,68,0.4)' : '0 2px 8px rgba(59,111,212,0.3)', animation: isListening ? 'pulse 1.5s ease infinite' : 'none' }}>
                  {isListening ? <Square size={12} fill="#fff" /> : <Mic size={12} />}
                  {isListening ? '録音停止' : '🎤 音声入力'}
                </button>
              )}
            </div>
            {isListening && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#dc2626' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1s ease infinite' }} />
                録音中... 話し終わったら「録音停止」を押してください
              </div>
            )}
            {speechError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#dc2626' }}>
                <AlertCircle size={14} />{speechError}
              </div>
            )}
            {!isSupported && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#059669' }}>
                💡 iPhoneはキーボードの🎤ボタンで音声入力できます
              </div>
            )}
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={isListening ? '🎤 話しかけてください...' : '今日学んだことや気づきを記録しましょう。\n音声入力ボタンで話すことでも入力できます。'} rows={4} style={{ width: '100%', border: `1.5px solid ${isListening ? '#fca5a5' : '#e5e7eb'}`, borderRadius: 10, padding: '12px 14px', fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.7, color: '#374151', background: isListening ? '#fff5f5' : '#fff' }} onFocus={e => { if(!isListening) e.target.style.borderColor='#3b6fd4'; }} onBlur={e => { if(!isListening) e.target.style.borderColor='#e5e7eb'; }} />
            <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{notes.length}文字</div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}><Star size={13} color="#f59e0b" /> 学習の質</label>
            <StarRating value={quality} onChange={setQuality} />
          </div>
          <button onClick={handleSave} disabled={saving || !finalHours} style={{ width: '100%', background: saved ? 'linear-gradient(135deg,#059669,#10b981)' : !finalHours ? '#e5e7eb' : 'linear-gradient(135deg,#1e40af,#3b6fd4)', color: !finalHours ? '#9ca3af' : '#fff', border: 'none', borderRadius: 12, padding: '14px', cursor: !finalHours ? 'not-allowed' : 'pointer', fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: !finalHours ? 'none' : '0 4px 14px rgba(30,64,175,0.3)', transition: 'all 0.3s', animation: saved ? 'savedAnim 0.4s ease' : 'none' }}>
            {saved ? <><CheckCircle size={18} /> 記録しました！</> : saving ? '保存中...' : <><Save size={18} /> 学習を記録する</>}
          </button>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#111827', display: 'flex', alignItems: 'center', gap: 7 }}><BookOpen size={15} color="#3b6fd4" /> 最近の学習記録</h2>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{stats.count}件</span>
          </div>
          {logsLoading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 }}>読み込み中...</div>
          ) : logs.length === 0 ? (
            <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '36px 20px', textAlign: 'center', color: '#9ca3af' }}>
              <BookOpen size={32} strokeWidth={1} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: 14 }}>まだ学習ログがありません</p>
              <p style={{ margin: '6px 0 0', fontSize: 12 }}>上のフォームで最初の学習を記録しましょう！</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {logs.map(log => <LogCard key={log.id} log={log} onDelete={handleDelete} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
